import { useState, useEffect, createContext, useContext } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth, ORG_TYPES } from "@/App";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard, Users, FileText, FolderOpen, BarChart3,
  Settings, LogOut, Menu, X, Bell, Plus, ChevronRight,
  UserCog, Building2, Shield, ClipboardList, Gavel, Share2,
  FileX, Plug, Sun, Moon, TrendingUp, Sparkles, GitCompare
} from "lucide-react";
import UserSwitcher from "./UserSwitcher";

// Theme context
const ThemeContext = createContext({ isDark: true, toggleTheme: () => {} });
export const useTheme = () => useContext(ThemeContext);

const getNavigation = (isAdmin, orgType) => {
  const baseNav = [];
  if (orgType === "provider") {
    baseNav.push(
      { name: "Coverage Intelligence", href: "/coverage-intelligence", icon: Shield },
      { name: "Auth/Referral Dashboard", href: "/pa-requests", icon: FileText },
      { name: "Denied Claims", href: "/denied-claims", icon: FileX },
      { name: "Appeals", href: "/appeals", icon: Gavel },
    );
  }
  if (orgType === "tpa" || orgType === "payer") {
    baseNav.push(
      { name: "Review Queue", href: "/review-queue", icon: ClipboardList },
      { name: "Auth/Referral Dashboard", href: "/pa-requests", icon: FileText },
      { name: "Appeals", href: "/appeals", icon: Gavel },
    );
  }
  baseNav.push(
    { name: "Form Library", href: "/form-library", icon: FolderOpen },
    { name: "Analytics", href: "/analytics", icon: BarChart3 },
    { name: "Insights", href: "/insights", icon: Sparkles },
    { name: "Provider Performance", href: "/provider-performance", icon: TrendingUp },
    { name: "Provider Compare", href: "/provider-compare", icon: GitCompare },
  );
  return baseNav;
};

// Theme palette
const T = (dark) => ({
  bg: dark ? "bg-[#0a0f1a]" : "bg-[#f5f6f8]",
  sidebar: dark ? "bg-[#0d1321] border-slate-800" : "bg-white border-gray-200",
  sidebarText: dark ? "text-slate-400" : "text-gray-600",
  sidebarHover: dark ? "hover:bg-slate-800 hover:text-white" : "hover:bg-gray-100 hover:text-gray-900",
  sidebarActive: dark ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" : "bg-blue-50 text-[#0066a1] border-blue-200",
  sidebarActiveDot: dark ? "text-emerald-400" : "text-[#0066a1]",
  header: dark ? "bg-[#0d1321]/80 border-slate-800" : "bg-white/80 border-gray-200",
  logo: dark ? "bg-emerald-500" : "bg-[#0066a1]",
  logoText: dark ? "text-white" : "text-[#0066a1]",
  orgBadge: dark ? "border-emerald-500/30 text-emerald-400 bg-emerald-500/10" : "border-blue-300 text-[#0066a1] bg-blue-50",
  newBtn: dark ? "bg-emerald-500 hover:bg-emerald-400 text-slate-900 shadow-emerald-500/20" : "bg-[#0066a1] hover:bg-[#005080] text-white shadow-blue-500/20",
  orgText: dark ? "text-slate-400" : "text-gray-500",
  userName: dark ? "text-white" : "text-gray-900",
  userRole: dark ? "text-slate-500" : "text-gray-500",
  bell: dark ? "text-slate-400 hover:text-white hover:bg-slate-800" : "text-gray-500 hover:text-gray-900 hover:bg-gray-100",
  bellDot: dark ? "bg-emerald-500" : "bg-[#0066a1]",
  dropdown: dark ? "bg-slate-900 border-slate-800" : "bg-white border-gray-200",
  dropdownItem: dark ? "text-slate-300 focus:bg-slate-800 focus:text-white" : "text-gray-700 focus:bg-gray-100 focus:text-gray-900",
  dropdownSep: dark ? "bg-slate-800" : "bg-gray-200",
  content: dark ? "" : "",
});

export const DashboardLayout = ({ children }) => {
  const { user, organization, logout, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem("dashboard_theme");
    return saved ? saved === "dark" : true;
  });

  useEffect(() => { localStorage.setItem("dashboard_theme", isDark ? "dark" : "light"); }, [isDark]);

  const navigation = getNavigation(isAdmin, organization?.org_type);
  const t = T(isDark);
  const getInitials = (name) => name?.split(" ").map(n => n[0]).join("").toUpperCase() || "U";
  const orgTypeLabel = ORG_TYPES[organization?.org_type]?.label || organization?.org_type;

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme: () => setIsDark(!isDark) }}>
      <div className={`min-h-screen ${t.bg} transition-colors duration-300`}>
        {/* Mobile sidebar backdrop */}
        {sidebarOpen && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
        )}

        {/* Sidebar */}
        <aside className={cn(
          "fixed top-0 left-0 z-50 h-full w-64 border-r transform transition-all duration-200 ease-in-out lg:translate-x-0",
          t.sidebar,
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}>
          <div className="flex flex-col h-full">
            {/* Logo */}
            <div className={`flex items-center justify-between h-16 px-4 border-b ${isDark ? "border-slate-800" : "border-gray-200"}`}>
              <Link to="/pa-requests" className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-lg ${t.logo} flex items-center justify-center`}>
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <span className={`font-semibold text-lg ${t.logoText}`}>Priorath</span>
              </Link>
              <button className={`lg:hidden p-1 rounded ${t.sidebarText}`} onClick={() => setSidebarOpen(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Organization Info */}
            {organization && (
              <div className={`px-4 py-3 border-b ${isDark ? "border-slate-800" : "border-gray-200"}`}>
                <div className={`flex items-center gap-2 ${t.orgText}`}>
                  <Building2 className="w-4 h-4" />
                  <span className="text-xs font-medium truncate">{organization.name}</span>
                </div>
                <Badge variant="outline" className={`mt-1 text-xs ${t.orgBadge}`}>{orgTypeLabel}</Badge>
              </div>
            )}

            {/* New PA Button */}
            {organization?.org_type === "provider" && (
              <div className="p-4">
                <Button className={`w-full font-semibold shadow-lg ${t.newBtn}`} onClick={() => navigate("/pa-requests/new")} data-testid="new-pa-button">
                  <Plus className="w-4 h-4 mr-2" /> New PA Request
                </Button>
              </div>
            )}

            {/* Navigation */}
            <nav className="flex-1 px-3 space-y-1 overflow-y-auto py-2">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link key={item.name} to={item.href}
                    data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                      isActive ? `${t.sidebarActive} border` : `${t.sidebarText} ${t.sidebarHover}`
                    )}>
                    <item.icon className={cn("w-5 h-5", isActive ? t.sidebarActiveDot : "")} />
                    {item.name}
                    {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
                  </Link>
                );
              })}
            </nav>

            {/* User section */}
            <div className={`p-4 border-t ${isDark ? "border-slate-800" : "border-gray-200"}`}>
              <div className="flex items-center gap-3 px-2">
                <Avatar className="h-9 w-9">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className={`${isDark ? "bg-emerald-500/20 text-emerald-400" : "bg-blue-100 text-[#0066a1]"} text-sm`}>
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${t.userName} truncate`}>{user?.name}</p>
                  <div className="flex items-center gap-1">
                    <Shield className={`w-3 h-3 ${t.userRole}`} />
                    <p className={`text-xs ${t.userRole} truncate capitalize`}>{user?.role}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </aside>

        {/* Main content */}
        <div className="lg:pl-64">
          {/* Top header */}
          <header className={`sticky top-0 z-30 h-16 backdrop-blur-md border-b ${t.header}`}>
            <div className="flex items-center justify-between h-full px-4 lg:px-6">
              <button className={`lg:hidden p-2 rounded-lg ${t.sidebarText}`} onClick={() => setSidebarOpen(true)}>
                <Menu className="w-5 h-5" />
              </button>
              <div className="flex-1 lg:flex-none" />
              <div className="flex items-center gap-2">
                {/* Theme Toggle */}
                <div className="flex items-center gap-1.5 mr-2" data-testid="dashboard-theme-toggle">
                  <Sun className={`w-4 h-4 ${isDark ? "text-slate-500" : "text-amber-500"}`} />
                  <Switch checked={isDark} onCheckedChange={setIsDark} />
                  <Moon className={`w-4 h-4 ${isDark ? "text-emerald-400" : "text-slate-400"}`} />
                </div>

                <UserSwitcher />

                <Button variant="ghost" size="icon" className={`relative ${t.bell}`}>
                  <Bell className="w-5 h-5" />
                  <span className={`absolute top-1 right-1 w-2 h-2 ${t.bellDot} rounded-full`} />
                </Button>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className={`gap-2 px-2 ${isDark ? "hover:bg-slate-800" : "hover:bg-gray-100"}`} data-testid="user-menu-trigger">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={user?.picture} />
                        <AvatarFallback className={`${isDark ? "bg-emerald-500/20 text-emerald-400" : "bg-blue-100 text-[#0066a1]"} text-xs`}>
                          {getInitials(user?.name)}
                        </AvatarFallback>
                      </Avatar>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className={`w-56 ${t.dropdown}`}>
                    <div className="px-2 py-1.5">
                      <p className={`text-sm font-medium ${t.userName}`}>{user?.name}</p>
                      <p className={`text-xs ${t.userRole}`}>{user?.email}</p>
                      <Badge variant="outline" className={`mt-1 text-xs ${isDark ? "border-slate-600 text-slate-400" : "border-gray-300 text-gray-500"}`}>
                        <Shield className="w-3 h-3 mr-1" />{user?.role}
                      </Badge>
                    </div>
                    <DropdownMenuSeparator className={t.dropdownSep} />
                    {isAdmin && (
                      <>
                        <DropdownMenuItem onClick={() => navigate("/user-management")} className={t.dropdownItem}>
                          <UserCog className="w-4 h-4 mr-2" /> User Management
                        </DropdownMenuItem>
                        <DropdownMenuSeparator className={t.dropdownSep} />
                      </>
                    )}
                    <DropdownMenuItem onClick={() => navigate("/settings")} className={t.dropdownItem}>
                      <Settings className="w-4 h-4 mr-2" /> Settings
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className={t.dropdownSep} />
                    <DropdownMenuItem onClick={logout} className="text-red-400 focus:bg-slate-800 focus:text-red-300" data-testid="logout-button">
                      <LogOut className="w-4 h-4 mr-2" /> Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </header>

          {/* Page content */}
          <main className="p-4 lg:p-6 animate-fade-in">
            {children}
          </main>
        </div>
      </div>
    </ThemeContext.Provider>
  );
};

export default DashboardLayout;
